"use client";

import { m as motion } from "framer-motion";
import Link from "next/link";
import { useState } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  ChevronDown,
  PhoneCall,
  Quote,
  Repeat,
  Rocket,
  X,
} from "lucide-react";
import { ProductJourney } from "@/components/startup/ProductJourney";
import { trackEvent } from "@/lib/track";
import type { FAQItem } from "@/lib/schema";

interface Props {
  bookingHref: string;
  prices: { mvp: number; monthly: number; quarterly: number; weekly: number };
  faqs: FAQItem[];
}

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.5 },
};

const SECTION = "section-padding pt-0 lg:pb-24";

const PROBLEMS = [
  {
    title: "Il socio tecnico che non arriva",
    text: "Cerchi un CTO da mesi. Intanto l'idea resta su una slide e il mercato non aspetta.",
  },
  {
    title: "Agenzie lente e care",
    text: "Preventivi a cinque cifre, mesi di analisi, e parli con un commerciale invece che con chi scrive il codice.",
  },
  {
    title: "Il prototipo che non regge",
    text: "L'hai fatto con Lovable, Bolt o un altro strumento AI. In demo funziona, con utenti veri si rompe.",
  },
];

const NOT_FOR = [
  "Chi cerca un cofondatore tecnico a tempo pieno: lì serve un socio, non un partner esterno.",
  "Chi non ha ancora parlato con un solo possibile cliente: prima quella parte, poi il codice.",
  "Chi ha bisogno di tutto per domani: una prima versione fatta bene richiede qualche settimana.",
];

const PROCESS = [
  { name: "Call di 30 minuti", text: "Mi racconti l'idea. Ti dico se ha senso costruirla adesso e da dove partire." },
  { name: "Perimetro scritto", text: "Cosa c'è e cosa no, tempi e prezzo, nero su bianco prima di iniziare." },
  { name: "Lavoro a settimane", text: "Ogni settimana ricevi lo stato scritto: cosa è fatto, cosa viene dopo." },
  { name: "Modifiche per iscritto", text: "Se cambia il perimetro, prima cambia la stima. Niente sorprese in fattura." },
];

export function StartupContent({ bookingHref, prices, faqs }: Props) {
  const trackCall = (location: string) =>
    trackEvent("book_call_click", { tool: "tidycal", location });

  const phases = [
    {
      name: "Idea",
      when: "30 minuti",
      price: "Gratis",
      gets: [
        "Capiamo bene il progetto e il suo obiettivo",
        "Decidiamo cosa serve nella prima versione e cosa può aspettare",
      ],
      next: "Si passa oltre quando il perimetro è chiaro e firmato.",
    },
    {
      name: "Prima versione",
      when: "4-6 settimane",
      price: `da ${prices.mvp} €`,
      gets: ["La funzione principale, funzionante", "Accessi utente e messa online", "Documentazione del codice"],
      next: "Si passa oltre quando i primi utenti la usano davvero.",
      link: { href: "/mvp", label: "Dettagli della prima versione" },
    },
    {
      name: "Primi clienti",
      when: "mese per mese",
      price: `da ${prices.quarterly} € / mese`,
      gets: [
        "Uno sviluppatore dedicato, una richiesta alla volta",
        `Mensile ${prices.monthly} €, trimestrale ${prices.quarterly} € al mese, oppure ${prices.weekly} € a settimana`,
      ],
      next: "Si passa oltre quando il prodotto vende e ti serve un team tuo.",
      link: { href: "/abbonamento", label: "Come funziona l'abbonamento" },
      highlight: true,
    },
    {
      name: "Il tuo team",
      when: "quando serve",
      price: "Senza vincoli",
      gets: ["Il codice è già tuo, sul tuo GitHub e documentato", "Se assumi uno sviluppatore, lo affianco nel passaggio"],
      next: "Il prodotto continua anche senza di me.",
    },
  ];

  return (
    <div>
      {/* ── INTESTAZIONE ───────────────────────────────────────── */}
      <section className="section-padding pt-8 md:pt-12 lg:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots" />
        <div className="container-wide relative">
          <div className="grid items-center gap-12 xl:grid-cols-[1.2fr_0.8fr]">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <span className="font-mono text-label text-primary mb-4 block">
                Per founder e PMI con un prodotto digitale
              </span>
              <h1 className="page-hero-title mb-5">
                Dalla prima versione al prodotto sul mercato, con{" "}
                <span className="text-shine">lo stesso partner tecnico</span>
              </h1>
              <p className="text-body-lg text-muted-foreground mb-8 max-w-2xl leading-relaxed">
                Costruisco la prima versione del tuo prodotto in 4-6 settimane, poi resto al tuo fianco per farlo
                crescere. Un solo referente tecnico, dall&apos;idea ai primi clienti.
              </p>

              <div className="mb-8 flex flex-wrap gap-2">
                <Chip icon={Rocket} label="Prima versione da" value={`€ ${prices.mvp}`} highlight />
                <Chip icon={Repeat} label="Abbonamento da" value={`€ ${prices.quarterly}/mese`} />
                <Chip icon={PhoneCall} label="Per iniziare" value="Call gratuita" />
              </div>

              <div className="flex flex-col gap-4 sm:flex-row">
                <Link
                  href={bookingHref}
                  onClick={() => trackCall("startup_hero")}
                  className="btn-glow inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-primary to-primary-glow px-7 font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
                >
                  Parliamo 30 minuti della tua idea
                  <PhoneCall className="h-4 w-4" />
                </Link>
                <a
                  href="#fasi"
                  className="rotating-border inline-flex h-12 items-center justify-center gap-2 rounded-xl px-7 font-semibold text-foreground"
                >
                  Vedi le fasi
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">Se non è il momento giusto, te lo dico.</p>
            </motion.div>

            <div className="flex justify-center">
              <ProductJourney />
            </div>
          </div>
        </div>
      </section>

      {/* ── IL PROBLEMA ────────────────────────────────────────── */}
      <section className={SECTION}>
        <div className="container-wide">
          <motion.div {...fadeUp} className="mb-8 max-w-2xl">
            <span className="font-mono text-label text-primary mb-3 block">Il punto di partenza</span>
            <h2 className="font-display text-h2 font-bold">Hai un'idea che regge. Ti manca chi la costruisce.</h2>
          </motion.div>
          <div className="grid gap-4 lg:grid-cols-3">
            {PROBLEMS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                className="surface-card p-6 md:p-7"
              >
                <h3 className="mb-2 font-display text-lg font-semibold">{p.title}</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{p.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── LE QUATTRO FASI ────────────────────────────────────── */}
      <section id="fasi" className={`${SECTION} scroll-mt-24`}>
        <div className="container-wide">
          <motion.div {...fadeUp} className="mb-10 max-w-2xl">
            <span className="font-mono text-label text-primary mb-3 block">Come si cresce</span>
            <h2 className="font-display text-h2 font-bold">Quattro fasi, un solo referente</h2>
            <p className="mt-3 text-muted-foreground">
              Ogni fase ha un risultato chiaro e un prezzo scritto. Passi alla successiva solo quando ha senso.
            </p>
          </motion.div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {phases.map((ph, i) => (
              <motion.div
                key={ph.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                className={`surface-card flex flex-col p-6 ${ph.highlight ? "ring-1 ring-primary/40" : ""}`}
              >
                <div className="mb-4 flex items-center gap-3">
                  <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary font-mono text-sm font-bold text-primary-foreground">
                    {i + 1}
                  </span>
                  <h3 className="font-display text-lg font-semibold">{ph.name}</h3>
                </div>
                <p className="font-display text-2xl font-bold text-foreground">{ph.price}</p>
                <p className="mb-4 text-xs text-muted-foreground">{ph.when}</p>
                <ul className="mb-4 space-y-2">
                  {ph.gets.map((g) => (
                    <li key={g} className="grid grid-cols-[16px_minmax(0,1fr)] items-start gap-2">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                      <span className="text-sm text-muted-foreground">{g}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-auto border-t border-[hsl(var(--border))] pt-3 text-xs leading-relaxed text-muted-foreground">
                  {ph.next}
                </p>
                {ph.link && (
                  <Link
                    href={ph.link.href}
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                  >
                    {ph.link.label}
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </Link>
                )}
              </motion.div>
            ))}
          </div>

          <motion.div
            {...fadeUp}
            className="surface-card mt-6 flex flex-col gap-5 border-orange-500/40 p-6 md:flex-row md:items-center md:justify-between md:gap-10 md:p-8"
          >
            <div className="max-w-2xl">
              <p className="mb-1.5 font-display text-lg font-semibold text-foreground">
                Hai già un prototipo fatto con l&apos;AI? Non si butta.
              </p>
              <p className="text-sm leading-relaxed text-muted-foreground md:text-base">
                Lo guardo, ti dico cosa tenere e cosa rifare, e quanto costa ognuna delle due strade.
              </p>
            </div>
            <Link
              href="/servizi/bug-fixing-ai-nocode"
              className="inline-flex h-12 shrink-0 items-center justify-center gap-2 rounded-xl bg-orange-500 px-6 font-semibold text-white transition-transform hover:-translate-y-0.5 hover:bg-orange-400"
            >
              Da no-code a prodotto
              <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── PER CHI NON È ──────────────────────────────────────── */}
      <section className={SECTION}>
        <div className="container-wide">
          <motion.div {...fadeUp} className="surface-card max-w-4xl p-6 md:p-8 xl:p-10">
            <span className="font-mono text-label text-primary mb-3 block">Per chi non è</span>
            <h2 className="mb-5 font-display text-2xl font-bold">Te lo dico prima, così non perdiamo tempo</h2>
            <ul className="space-y-3">
              {NOT_FOR.map((item) => (
                <li key={item} className="grid grid-cols-[18px_minmax(0,1fr)] items-start gap-3">
                  <X className="mt-1 h-4 w-4 shrink-0 text-[hsl(var(--destructive))]" />
                  <span className="text-sm leading-relaxed text-foreground/90 md:text-base">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* ── COME LAVORIAMO ─────────────────────────────────────── */}
      <section className={SECTION}>
        <div className="container-wide">
          <motion.div {...fadeUp} className="mb-10 max-w-2xl">
            <span className="font-mono text-label text-primary mb-3 block">Il metodo</span>
            <h2 className="font-display text-h2 font-bold">Come lavoriamo, in pratica</h2>
          </motion.div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {PROCESS.map((step, i) => (
              <motion.div
                key={step.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className="surface-card p-6"
              >
                <span className="mb-3 block font-mono text-sm font-bold text-primary">0{i + 1}</span>
                <h3 className="mb-2 font-display text-base font-semibold">{step.name}</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{step.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROVE ──────────────────────────────────────────────── */}
      <section className={SECTION}>
        <div className="container-wide">
          <motion.div {...fadeUp} className="mb-8 max-w-2xl">
            <span className="font-mono text-label text-primary mb-3 block">Chi c'è dietro</span>
            <h2 className="font-display text-h2 font-bold">Fatti, non promesse</h2>
          </motion.div>
          <div className="grid gap-4 lg:grid-cols-3">
            <motion.div {...fadeUp} className="surface-card p-6 md:p-7">
              <h3 className="mb-2 font-display text-lg font-semibold">Una piattaforma per una startup estera</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Dalla prima versione alla produzione, poi sviluppo continuo a canone. Per riservatezza non dico di
                cosa si tratta: è lo stesso rispetto che avrò per il tuo progetto.
              </p>
            </motion.div>
            <motion.div {...fadeUp} className="surface-card p-6 md:p-7">
              <h3 className="mb-2 font-display text-lg font-semibold">Cinque anni in Accenture</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Come sviluppatore su progetti per Saipem e Nexi/PagoPA: software che non può fermarsi. Il metodo di
                lavoro viene da lì, il rapporto diretto da freelance.
              </p>
            </motion.div>
            <motion.figure {...fadeUp} className="surface-card flex flex-col p-6 md:p-7">
              <Quote className="mb-3 h-5 w-5 text-primary/70" />
              <blockquote className="text-sm leading-relaxed text-foreground/90">
                Ho lavorato con Dario per lo sviluppo di un&apos;applicazione per telefono e della pipeline di
                comunicazione e storage dati, e non potevo chiedere di meglio. Competenza, disponibilità e
                professionalità. Super suggerito!
              </blockquote>
              <figcaption className="mt-auto pt-4 text-xs text-muted-foreground">
                Andrea Bracali ·{" "}
                <a
                  href="https://www.neurashieldtechnology.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                >
                  neurashieldtechnology.com
                  <ArrowUpRight className="h-3 w-3" />
                </a>
              </figcaption>
            </motion.figure>
          </div>
        </div>
      </section>

      {/* ── FAQ ────────────────────────────────────────────────── */}
      <section className={SECTION}>
        <div className="container-wide">
          <motion.div {...fadeUp} className="mb-8 max-w-2xl">
            <span className="font-mono text-label text-primary mb-3 block">Domande frequenti</span>
            <h2 className="font-display text-h2 font-bold">Quello che chiedono i founder</h2>
          </motion.div>
          <div className="max-w-2xl space-y-3">
            {faqs.map((f) => (
              <FaqRow key={f.question} item={f} />
            ))}
          </div>
        </div>
      </section>

      {/* ── INVITO FINALE ──────────────────────────────────────── */}
      <section className="section-padding pb-24 md:pb-32">
        <div className="container-wide">
          <motion.div {...fadeUp} className="surface-card relative max-w-4xl overflow-hidden p-8 md:p-12">
            <div className="pointer-events-none absolute inset-0 bg-primary opacity-[0.07]" />
            <div className="relative max-w-2xl">
              <h2 className="font-display text-2xl font-bold md:text-4xl">Parliamo 30 minuti della tua idea</h2>
              <p className="mt-4 text-muted-foreground">
                Se non è il momento giusto, te lo dico. Se lo è, esci dalla call con i prossimi passi e un'idea
                chiara di tempi e costi.
              </p>
              <div className="mt-7 flex flex-col gap-4 sm:flex-row">
                <Link
                  href={bookingHref}
                  onClick={() => trackCall("startup_cta_final")}
                  className="btn-glow inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-primary to-primary-glow px-7 font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5 sm:w-auto"
                >
                  Prenota la call
                  <PhoneCall className="h-4 w-4 shrink-0" />
                </Link>
                <Link
                  href="/mvp"
                  className="rotating-border inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl px-7 font-semibold text-foreground sm:w-auto"
                >
                  Vedi la prima versione
                  <ArrowRight className="h-4 w-4 shrink-0" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

function Chip({
  icon: Icon,
  label,
  value,
  highlight,
}: {
  icon: typeof Rocket;
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 ${
        highlight ? "border-primary/40 bg-primary/5" : "border-[hsl(var(--border))] bg-surface/40"
      }`}
    >
      <Icon className={`h-3.5 w-3.5 shrink-0 ${highlight ? "text-primary" : "text-muted-foreground"}`} />
      <span className="whitespace-nowrap text-[0.6875rem] text-muted-foreground">{label}</span>
      <span className="whitespace-nowrap text-[0.8125rem] font-semibold text-foreground">{value}</span>
    </div>
  );
}

function FaqRow({ item }: { item: FAQItem }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="surface-card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 p-5 text-left"
      >
        <span className="font-display text-base font-semibold">{item.question}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <p className="px-5 pb-5 text-sm leading-relaxed text-muted-foreground">{item.answer}</p>}
    </div>
  );
}
